import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { Card } from '~/eds/src/components/Card/Card';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { FieldInfo } from '~/features/fields/FieldInfo/FieldInfo';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

import { INFO_LABEL_FLEX, INFO_SPACING, INFO_WIDTH } from '../../constants';
import { DEFAULT_FIELD_VALUE } from '../../fields/constants';
import { FieldValueDisplay } from '../../fields/shared/FieldValue/FieldValueDisplay';
import { testIsTestCaseEvaluating } from '../../fields/utils';
import { TableValueDisplay } from '../../ModelDetails/TableDetailsPanel/TableValueDisplay';
import { PromptModelTestCase } from '../../types/deprecated';
import { ReviewHighlightSources } from './ReviewHighlightSources';
import { ReviewLlmOutput } from './ReviewLlmOutput';

interface Props {
  testCase: PromptModelTestCase;
}

export const ReviewTestCase = ({ testCase }: Props) => {
  const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
  const { getSearchParam } = useRouting();
  const isTableModel =
    enableTableExtraction && getSearchParam('type') === 'table';

  const intl = useIntl();
  const isLoading = testIsTestCaseEvaluating(testCase);

  const dispatch = useDispatch();

  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const tableConfig = useSelector(
    slices.xRay.selectors.selectTableConfiguration,
  );
  const isClassificationModel = Boolean(config.fieldClassification);
  const documentVersionId = testCase?.entity.id
    ? Number(testCase.entity.id)
    : null;

  const documentHighlights = isTableModel
    ? // For table models, each table cell can potentially have a highlight
      // so we flatten them into a single array of well-defined highlights
      testCase.tableValue
      ? testCase.tableValue.value
          .flatMap((tableRow) =>
            tableRow.cells.flatMap((tableCell) => tableCell.highlights),
          )
          .filter((highlight) => !!highlight)
      : []
    : // For non-table models, always read source highlights from gold value
      // if populated, otherwise fall back on model value highlights
      (testCase.goldValue?.highlights ?? testCase.modelValue?.highlights ?? []);
  const activeHighlight = documentHighlights?.[0];

  const {
    data: document,
    error: documentError,
    isFetching: isLoadingActiveDocument,
  } = api.endpoints.getDocumentFromVersion.useQuery(documentVersionId!, {
    skip: !documentVersionId,
  });

  const documentViewer = useSelector(
    slices.xRay.selectors.selectDocumentViewer,
  );

  useEffect(() => {
    // only update active highlight if it's not already set, or when the document version changes.
    // this is to prevent the active highlight from changing when tagging and deleting sources.
    if (
      !documentViewer.activeHighlightId ||
      documentVersionId !== documentViewer.documentVersionId
    ) {
      dispatch(
        slices.xRay.actions.setDocumentViewer({
          activeHighlightId: activeHighlight?.id,
          documentVersionId,
          highlights: activeHighlight ? [activeHighlight] : [],
        }),
      );
    }
  }, [testCase, documentVersionId, activeHighlight]);

  const fieldValue =
    testCase?.state === 'error'
      ? {
          value: intl.formatMessage({
            id: 'ReviewTestCase.errorRetryEvaluation',
            defaultMessage: 'Error, please Retry Evaluation.',
            description: 'Error message when test case evaluation fails',
          }),
        }
      : testCase?.modelValue;

  return (
    <Layout direction="column" spacing={1}>
      <Layout align="center" spacing={INFO_SPACING}>
        <Text flex={INFO_LABEL_FLEX} variant="body-bold">
          {intl.formatMessage({
            id: 'ReviewTestCase.document',
            defaultMessage: 'Document',
            description: 'Label for document field',
          })}
        </Text>
        {documentError ? (
          <TruncateText>
            {intl.formatMessage({
              id: 'ReviewTestCase.notAvailable',
              defaultMessage: 'Not available',
              description: 'Text shown when document is not available',
            })}
          </TruncateText>
        ) : document && !isLoadingActiveDocument ? (
          <Link
            enableNewTab
            shouldTruncate
            pathname={`/document/${document.id}`}
          >
            {document.name}
          </Link>
        ) : (
          <LoadingShimmer height="14px" />
        )}
      </Layout>
      <Layout align="center" spacing={INFO_SPACING}>
        <Text flex={INFO_LABEL_FLEX} variant="body-bold">
          {isTableModel
            ? intl.formatMessage({
                id: 'ReviewTestCase.table',
                defaultMessage: 'Table',
                description: 'Label for table section',
              })
            : intl.formatMessage({
                id: 'ReviewTestCase.field',
                defaultMessage: 'Field',
                description: 'Label for field section',
              })}
        </Text>
        <Box minW={INFO_WIDTH} py={INFO_SPACING}>
          {isTableModel ? (
            <Card mode="bordered" size="s" status="info" width="100%">
              <Layout align="center" spacing={2}>
                <Icon icon="table" />
                <Layout direction="column">
                  <Text>{tableConfig.tableSpecState.name}</Text>
                  <Text preset="help">
                    <FormattedMessage
                      id="ReviewTestCase.TableEntityLabel"
                      defaultMessage="Table"
                      description="Label for the table entity type"
                    />
                  </Text>
                </Layout>
              </Layout>
            </Card>
          ) : (
            config?.field && <FieldInfo id={config.field.id} />
          )}
        </Box>
      </Layout>
      <Layout
        align="flex-start"
        spacing={INFO_SPACING}
        direction={
          isTableModel && !isLoading && testCase.tableValue?.value.length
            ? 'column'
            : 'row'
        }
      >
        <Text
          flex={
            isTableModel && !isLoading && testCase.tableValue?.value.length
              ? undefined
              : INFO_LABEL_FLEX
          }
          variant="body-bold"
        >
          {isClassificationModel
            ? intl.formatMessage({
                id: 'ReviewTestCase.identifiedValues',
                defaultMessage: 'Identified Value(s)',
                description:
                  'Label for identified values in classification model',
              })
            : intl.formatMessage({
                id: 'ReviewTestCase.value',
                defaultMessage: 'Value',
                description: 'Label for value in extraction model',
              })}
        </Text>
        {isLoading || (!isTableModel && typeof fieldValue === 'undefined') ? (
          <LoadingShimmer height="14px" />
        ) : isTableModel ? (
          <TableValueDisplay tableValue={testCase.tableValue} />
        ) : (
          <FieldValueDisplay fieldValue={fieldValue ?? DEFAULT_FIELD_VALUE} />
        )}
      </Layout>
      <ReviewHighlightSources
        documentVersionId={documentVersionId}
        highlights={documentHighlights}
        isLoading={isLoading}
      />
      <ReviewLlmOutput testCase={testCase} />
    </Layout>
  );
};
